import React from "react";
import FilterBoxWithDropdown from "../../molecules/filter-dropdowns/filter-dropdown";
import useBillingStore from "../../stores/billing-store/billing-store";
import { capitalizeFirstLetter } from "../../utils/string-utils";

type TServicesUsedObject = Record<
  string,
  { id: number; name: string; price: number; count: number }
>;

const ServiceFilterDropdown = () => {
  const [showDropdown, setShowDropdown] = React.useState(false);

  const { receiptEntities, serviceFilters, setServiceFilters } =
    useBillingStore();

  const listOfServicesWithPrice = React.useMemo(() => {
    const objectListOfServicesWithPrice = receiptEntities.reduce(
      (objectSoFar, receipt) => {
        const { receiptSkus, receipt_amount, additional_discount_amount } =
          receipt;

        const summaryForThisReceipt: TServicesUsedObject = {};

        receiptSkus.forEach((sku) => {
          const skuItemId = sku.sku_item.service_id as number;
          if (summaryForThisReceipt[skuItemId]) {
            summaryForThisReceipt[skuItemId] = {
              ...summaryForThisReceipt[skuItemId],
              price: summaryForThisReceipt[skuItemId].price + sku.net_amount,
              count: summaryForThisReceipt[skuItemId].count + 1,
            };
          } else {
            summaryForThisReceipt[skuItemId] = {
              id: skuItemId,
              name: sku.sku_item.service_name,
              price: sku.net_amount,
              count: 1,
            };
          }
        });

        Object.keys(summaryForThisReceipt).forEach((summaryId) => {
          const skuSummary = summaryForThisReceipt[summaryId];
          const { id, price, count, name } = skuSummary;

          if (!Number(receipt_amount)) {
            if (objectSoFar[id]) {
              objectSoFar[id] = {
                ...objectSoFar[id],
                count: objectSoFar[id].count + count,
              };
            } else {
              objectSoFar[id] = {
                id,
                name: name,
                price: 0,
                count,
              };
            }
          } else {
            const priceProportionForSku = price / Number(receipt_amount);
            const discountProportionated =
              additional_discount_amount * priceProportionForSku;
            const netAmountProportionated = price - discountProportionated;
            if (objectSoFar[id]) {
              objectSoFar[id] = {
                ...objectSoFar[id],
                price: objectSoFar[id].price + netAmountProportionated,
                count: objectSoFar[id].count + count,
              };
            } else {
              objectSoFar[id] = {
                id,
                name: name,
                price: netAmountProportionated,
                count,
              };
            }
          }
        });

        return objectSoFar;
      },
      {} as TServicesUsedObject
    );

    return Object.keys(objectListOfServicesWithPrice).map((serviceKey) => {
      const serviceItem = objectListOfServicesWithPrice[serviceKey];
      return {
        id: serviceItem.id,
        label: `${serviceItem.name} (${serviceItem.count})`,
        labelSubtext:
          `₹${Math.round(serviceItem.price).toLocaleString("en-IN")}` || "",
      };
    });
  }, [receiptEntities]);

  const handleSelectedOptions = (options: Array<string | number>) => {
    setServiceFilters(options as Array<number>);
  };

  const filterHeading = React.useMemo(() => {
    if (serviceFilters.length === 0 || showDropdown) {
      return "Service";
    }

    return serviceFilters
      .map((serviceFilter) =>
        capitalizeFirstLetter(
          listOfServicesWithPrice
            ?.find((service) => service.id === serviceFilter)
            ?.label.split(" (")[0] || ""
        )
      )
      .join(", ");
  }, [listOfServicesWithPrice, serviceFilters, showDropdown]);

  return (
    <FilterBoxWithDropdown
      title={filterHeading}
      options={listOfServicesWithPrice}
      selectedOptions={serviceFilters}
      setSelectedOptions={handleSelectedOptions}
      showDropdown={showDropdown}
      toggleDropdown={() => {
        setShowDropdown(!showDropdown);
      }}
      closeDropdown={() => {
        setShowDropdown(false);
      }}
    />
  );
};

export default ServiceFilterDropdown;
